/**
 * Extract a human-readable message from API response data, handling every
 * shape our backend returns. Ported from front-v2's `extractMessageFromData`
 * (src/utils/general/general.ts) so error handling is consistent across apps.
 */
// Joins an array of strings ("Unable to invite users…" style DRF messages)
// into one display string; null when the value isn't a non-empty string array.
function joinStringArray(value: unknown): string | null {
  if (!Array.isArray(value)) return null;
  const strings = value.filter(
    (item): item is string => typeof item === "string" && item.length > 0,
  );
  return strings.length > 0 ? strings.join("; ") : null;
}

function extractMessageFromData(responseData: unknown): string | null {
  if (!responseData) return null;

  // 1. Custom message field (most common) — string or array of strings.
  //    Must be handled BEFORE the generic field loop (#6), which would
  //    otherwise prefix it as "message: …".
  if (typeof responseData === "object" && "message" in responseData) {
    const { message } = responseData as { message: unknown };
    if (typeof message === "string" && message) return message;
    const joined = joinStringArray(message);
    if (joined) return joined;
  }

  // 2. String responses
  if (typeof responseData === "string") {
    return responseData;
  }

  // 3. detail field (DRF standard) — string or array of strings
  if (typeof responseData === "object" && "detail" in responseData) {
    const { detail } = responseData as { detail: unknown };
    if (typeof detail === "string" && detail) return detail;
    const joined = joinStringArray(detail);
    if (joined) return joined;
  }

  // 4. non_field_errors (DRF standard) — before the generic field loop so the
  //    user sees "Bad combination." rather than "non_field_errors: Bad …".
  if (typeof responseData === "object" && "non_field_errors" in responseData) {
    const joined = joinStringArray(
      (responseData as { non_field_errors: unknown }).non_field_errors,
    );
    if (joined) return joined;
  }

  // 5. Legacy handle field errors — also shadowed by the field loop otherwise.
  if (typeof responseData === "object" && "handle" in responseData) {
    const handle = (responseData as { handle: unknown }).handle;
    if (Array.isArray(handle) && handle.length > 0) {
      return String(handle[0]);
    }
  }

  // 6. Array format for bulk operations: [{ field: ["error"] }] or ["msg"]
  if (Array.isArray(responseData) && responseData.length > 0) {
    const firstItem = responseData[0];
    if (typeof firstItem === "object" && firstItem !== null) {
      const fieldErrors: string[] = [];
      for (const fieldValue of Object.values(firstItem)) {
        if (Array.isArray(fieldValue) && fieldValue.length > 0) {
          fieldErrors.push(fieldValue.join(", "));
        } else if (typeof fieldValue === "string" && fieldValue) {
          fieldErrors.push(fieldValue);
        }
      }
      if (fieldErrors.length > 0) return fieldErrors.join("; ");
    }
    return String(responseData[0]);
  }

  // 7. Standard DRF validation errors: { field: ["error"] } — runs LAST among
  //    object shapes; the well-known keys above must never reach this loop.
  //    We surface the messages only (no "field_name: " prefix — toasts read
  //    cleaner, and DRF messages are self-describing).
  if (typeof responseData === "object" && !Array.isArray(responseData)) {
    const fieldErrors: string[] = [];
    for (const fieldValue of Object.values(responseData)) {
      if (Array.isArray(fieldValue) && fieldValue.length > 0) {
        const joined = fieldValue.filter(Boolean).join(", ");
        if (joined) fieldErrors.push(joined);
      } else if (typeof fieldValue === "string" && fieldValue) {
        fieldErrors.push(fieldValue);
      } else if (typeof fieldValue === "object" && fieldValue !== null) {
        for (const nestedValue of Object.values(fieldValue)) {
          if (Array.isArray(nestedValue) && nestedValue.length > 0) {
            fieldErrors.push(nestedValue.join(", "));
          } else if (typeof nestedValue === "string" && nestedValue) {
            fieldErrors.push(nestedValue);
          }
        }
      }
    }
    if (fieldErrors.length > 0) return fieldErrors.join("; ");
  }

  return null;
}

export function getErrorMessage(error: any, msgFallback: string): string {
  // A real (or axios-shaped) error: parse the response body, not the generic
  // "Request failed with status code 4xx".
  if (error?.isAxiosError || error?.response) {
    return (
      extractMessageFromData(error.response?.data) ||
      error.response?.statusText ||
      error.message ||
      msgFallback
    );
  }

  // Our axios response interceptor (src/api/index.js) rejects with an
  // already-parsed value — a plain string, or the raw DRF array/object — so
  // parse the error itself.
  return (
    extractMessageFromData(error) ||
    (typeof error?.message === "string" ? error.message : undefined) ||
    msgFallback
  );
}
